/**
 * Product analytics — US deployment only.
 * CIS (152-FZ): PostHog is not initialized; no analytics payloads are sent abroad.
 *
 * Privacy (WP7): autocapture off, session recording off, mask DOM text/attributes.
 * Case text still goes to the API/LLM unchanged — PostHog masking does not affect that path.
 */
import { currentRegion } from "../config/regions";

let initialized = false;

/** Keys that must never appear in PostHog event properties. */
export const TRACK_DENYLIST = new Set([
  "query_text",
  "case_text",
  "summary",
  "diagnosis",
  "recommendation",
  "email",
  "message",
  "question",
  "answer",
  "extracted_text",
  "patient_message",
  "physician_summary",
  "health_profile",
  "text",
  "body",
  "content",
  "medications",
  "allergies",
  "chronic_conditions",
  "chronic_conditions_other",
  "surgeries_recent",
  "height_cm",
  "weight_kg",
  "smoking",
  "alcohol",
  "activity_level",
  "activity_days_per_week",
  "sleep_hours_typical",
  "sleep_regular",
  "notes",
  "notes_other",
  "none_reported",
  "display_name",
  "name",
]);

/** Keys allowed even if the value is a longer string (opaque ids / enums). */
export const TRACK_ALLOWLIST = new Set([
  "query_id",
  "user_id",
  "org_id",
  "tier",
  "plan",
  "error_code",
  "code",
  "status",
  "event",
  "source",
  "kind",
  "category",
  "format",
  "expires_in",
  "require_code",
  "region",
  "subject_kind",
  "subject_mode",
  "section",
  "surface",
  "scenario",
]);

const MAX_STRING_VALUE_LEN = 64;

function posthogReady() {
  return (
    typeof window !== "undefined" &&
    window.posthog &&
    typeof window.posthog.capture === "function"
  );
}

/**
 * Sanitize props for PostHog: drop denylisted keys and long free-text values.
 * Returns null when nothing safe remains (caller should skip capture).
 */
export function sanitizeTrackProps(props) {
  if (!props || typeof props !== "object") return {};
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (TRACK_DENYLIST.has(key)) continue;
    if (value == null) {
      out[key] = value;
      continue;
    }
    if (typeof value === "string") {
      const allowlisted = TRACK_ALLOWLIST.has(key);
      if (!allowlisted && value.length > MAX_STRING_VALUE_LEN) {
        continue;
      }
      // Email-shaped values are dropped only for non-allowlisted keys so
      // trusted ids (e.g. query_id) are never filtered by coincidence.
      if (
        !allowlisted &&
        key !== "email" &&
        value.includes("@") &&
        value.includes(".")
      ) {
        continue;
      }
      out[key] = value;
      continue;
    }
    if (
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint"
    ) {
      out[key] = value;
      continue;
    }
    // Objects/arrays are too easy to smuggle free text — skip.
  }
  return out;
}

export function looksLikeEmail(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return v.includes("@") && v.includes(".");
}

/**
 * Identify with an opaque user id only. E-mail-shaped values are rejected.
 */
export function identifyUser(userId) {
  if (currentRegion !== "us" || !posthogReady()) return;
  if (!userId || typeof userId !== "string" || looksLikeEmail(userId)) return;
  try {
    window.posthog.identify(userId);
  } catch (_) {
    /* analytics must never break the app */
  }
}

const HEALTH_SECTIONS = new Set(["safety", "history", "lifestyle"]);
const HEALTH_SURFACES = new Set(["row", "quick_card", "add_person", "profile_page"]);
const HEALTH_SAVE_STATUSES = new Set(["answered", "none"]);
const HEALTH_SUBJECT_KINDS = new Set(["self", "dependent"]);

/**
 * Props for health-section analytics. Only section, surface, subject kind,
 * and save status are copied — names and field values are ignored.
 */
export function healthSectionEventProps({
  section,
  surface,
  subjectKind,
  status,
} = {}) {
  const props = {};
  if (HEALTH_SECTIONS.has(section)) props.section = section;
  if (HEALTH_SURFACES.has(surface)) props.surface = surface;
  if (HEALTH_SUBJECT_KINDS.has(subjectKind)) props.subject_kind = subjectKind;
  if (HEALTH_SAVE_STATUSES.has(status)) props.status = status;
  return props;
}

export function trackHealthSectionShown(args) {
  track("health_section_shown", healthSectionEventProps(args));
}

export function trackHealthSectionSaved(args) {
  track("health_section_saved", healthSectionEventProps(args));
}

export function trackHealthSectionSkipped({ section, surface, subjectKind } = {}) {
  track(
    "health_section_skipped",
    healthSectionEventProps({ section, surface, subjectKind }),
  );
}

/**
 * Safe capture wrapper — strips PHI-prone keys/values before sending.
 */
export function track(event, props = {}) {
  if (currentRegion !== "us" || !posthogReady()) return;
  if (!event || typeof event !== "string") return;
  const safe = sanitizeTrackProps(props);
  try {
    window.posthog.capture(event, safe);
  } catch (_) {
    /* analytics must never break the app */
  }
}

/** Product analytics stay off the public doctor share page. */
export function shouldLoadProductAnalytics(pathname) {
  const path = pathname || "";
  return path !== "/share" && !path.startsWith("/share/");
}

export function initAnalytics() {
  if (initialized || currentRegion !== "us") return;

  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  if (!apiKey) return;

  initialized = true;
  const apiHost = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src =
            s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
            "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o =
              "init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag isFeatureEnabled reloadFeatureFlags identify reset get_distinct_id get_session_id startSessionRecording stopSessionRecording opt_out_capturing has_opted_out_capturing".split(
                " ",
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */

  window.posthog.init(apiKey, {
    api_host: apiHost,
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: true,
    disable_session_recording: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
  });
}
